import {
    CompetitionContact,
    CompetitionDetail,
    CompetitionLocation,
    CompetitionOrganizer,
    CompetitionRef,
    CompetitionSecurityData
} from "@/modules/competition/service/CompetitionService";
import {httpService} from "@/services/HttpService";
import {PersonRef, PersonSearchResultItem} from "@/shared/domain/Person";
import {RefWithName, RefWithNameAndAvatar} from "@/utils/Utils";
import {SportTypeVariant} from "@/modules/federation/services/SportTypeVariantService";
import {Contact, PriceProfile} from "@/shared/domain/General";
import {StartlistStatus} from "@/modules/competition/service/ManageStartlistService";
import {CustomAttribute} from "@/shared/domain/CustomAttributes";

class ManageCompetitionService {
    createUploadAvatarUrl(ref: CompetitionRef): string {
        return `/competition/manage/${ref}/avatar`
    }

    getCompetition(ref: CompetitionRef): Promise<CompetitionDetail> {
        return httpService.get(`/competition/manage/${ref}`)
    }

    getSecurity(ref: CompetitionRef): Promise<CompetitionSecurityData> {
        return httpService.get(`/competition/manage/${ref}/security`)
    }

    getCompetitionCode(ref: CompetitionRef): Promise<string> {
        return httpService.get(`/competition/manage/${ref}/code`)
    }

    delete(ref: CompetitionRef): Promise<void> {
        return httpService.delete(`/competition/manage/${ref}`)
    }

    publish(ref: CompetitionRef, request: CompetitionPublishRequest): Promise<CompetitionStatus> {
        return httpService.post(`/competition/manage/${ref}/publish`, request)
    }

    hide(ref: CompetitionRef): Promise<CompetitionStatus> {
        return httpService.post(`/competition/manage/${ref}/hide`, {})
    }

    archive(ref: CompetitionRef): Promise<CompetitionStatus> {
        return httpService.post(`/competition/manage/${ref}/archive`, {})
    }

    cancel(ref: CompetitionRef, reason: string): Promise<CompetitionStatus> {
        return httpService.post(`/competition/manage/${ref}/cancel`, {reason: reason})
    }

    lock(ref: CompetitionRef): Promise<CompetitionStatus> {
        return httpService.post(`/competition/manage/${ref}/lock`, {})
    }

    getShares(ref: CompetitionRef): Promise<Array<CompetitionShare>> {
        return httpService.get(`/competition/manage/${ref}/share`)
    }

    share(ref: CompetitionRef, person: PersonRef, role: CompetitionManageRole): Promise<CompetitionShare> {
        return httpService.post(`/competition/manage/${ref}/share`, {personRef: person, role: role})
    }

    unshare(ref: CompetitionRef, person: PersonRef): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/unshare`, {personRef: person})
    }

    updateShare(ref: CompetitionRef, person: PersonRef, role: CompetitionManageRole, starts: string, expires: string): Promise<CompetitionShare> {
        return httpService.put(`/competition/manage/${ref}/share`, {
            personRef: person,
            role: role,
            starts: starts,
            expires: expires
        })
    }

    getHistory(ref: CompetitionRef): Promise<Array<CompetitionHistory>> {
        return httpService.get(`/competition/manage/${ref}/history`)
    }

    getGeneralSettings(ref: CompetitionRef): Promise<CompetitionGeneralSettings> {
        return httpService.get(`/competition/manage/${ref}/settings/general`)
    }

    updateGeneralSettings(ref: CompetitionRef, settings: CompetitionGeneralSettings): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/settings/general`, settings)
    }

    updateCompetitionBanner(ref: CompetitionRef, banner: File): Promise<string> {
        return httpService.upload(`/competition/manage/${ref}/settings/banner`, banner)
    }

    getLiveSettings(ref: CompetitionRef): Promise<CompetitionLiveSettings> {
        return httpService.get(`/competition/manage/${ref}/settings/live`)
    }

    updateLiveSettings(ref: CompetitionRef, settings: CompetitionLiveSettings): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/settings/live`, settings)
    }

    getFinanceSettings(ref: CompetitionRef): Promise<CompetitionFinanceSettings> {
        return httpService.get(`/competition/manage/${ref}/settings/finance`)
    }

    updateFinanceSettings(ref: CompetitionRef, settings: CompetitionFinanceSettings): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/settings/finance`, settings)
    }

    getOfficialsSettings(ref: CompetitionRef): Promise<Array<CompetitionContact>> {
        return httpService.get(`/competition/manage/${ref}/settings/officials`)
    }

    updateOfficialsSettings(ref: CompetitionRef, officials: Array<CompetitionContact>): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/settings/officials`, {officials: officials})
    }

    getRegistrationSettings(ref: CompetitionRef): Promise<CompetitionRegistrationSettingsResponse> {
        return httpService.get(`/competition/manage/${ref}/settings/registration`)
    }

    updateRegistrationSettings(ref: CompetitionRef, settings: CompetitionRegistrationSettingsResponse): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/settings/registration`, settings)
    }

    getAppRoutes(ref: CompetitionRef): Promise<AppDataRoutesMetadata> {
        return httpService.get(`/competition/manage/${ref}/settings/approutes`)
    }

    updateAppRoutes(ref: CompetitionRef, body: {
        competitions: Array<CompetitionAppRoute>,
        activities: Array<ActivityAppRoute>
    }): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/settings/approutes`, body)
    }

    getCustomAttributes(ref: CompetitionRef): Promise<Array<CustomAttribute>> {
        return httpService.get(`/competition/manage/${ref}/settings/customattributes`)
    }

    updateCustomAttributesSettings(ref: CompetitionRef, customAttributes: Array<CustomAttribute>): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/settings/customattributes`, {
            customAttributes: customAttributes
        })
    }

    getSpecialSettings(ref: CompetitionRef): Promise<CompetitionSpecialSettings> {
        return httpService.get(`/competition/manage/${ref}/settings/special`)
    }

    updateSpecialSettings(ref: CompetitionRef, settings: CompetitionSpecialSettings): Promise<void> {
        return httpService.post(`/competition/manage/${ref}/settings/special`, settings)
    }
}

export enum CompetitionManageRole {
    ADMINISTRATOR = "ADMINISTRATOR",
    COLLABORATOR = "COLLABORATOR",
    RESULT_PROCESSOR = "RESULT_PROCESSOR"
}

export interface CompetitionShare {
    person: PersonSearchResultItem,
    role: CompetitionManageRole,
    active: boolean,
    expires: string,
    starts: string
}

export enum CompetitionStatus {
    DRAFT = "DRAFT",
    PUBLISHED = "PUBLISHED",
    LOCKED = "LOCKED",
    CANCELLED = "CANCELLED",
    ARCHIVED = "ARCHIVED",
    HIDDEN = "HIDDEN",
    DELETED = "DELETED"
}

export interface CompetitionHistory {
    ref: string,
    date: string,
    person: RefWithNameAndAvatar,
    message: string,
}

export enum CompetitionManageMenuStructureType {
    TIMESCHEDULE = "TIMESCHEDULE",
    STARTLIST = "STARTLIST",
    RESULTS = "RESULTS",
    RANKING = "RANKING"
}

export interface CompetitionManageMenuStructure {
    type: CompetitionManageMenuStructureType,
    items: Array<CompetitionManageSportTypeVariantMenuGroup>
}

export interface CompetitionManageSportTypeVariantMenuGroup {
    sportTypeVariant: SportTypeVariant,
    items: Array<CompetitionTestMenuItem>,
    attributes: any//Map<string, any>
}

export interface CompetitionTestMenuItem {
    ref: string,
    name: string,
    tests: Array<CompetitionTestMenuItem>,
    buckets: Array<CompetitionTestBucketMenuItem>,
    attributes: any//Map<string, any>
}

export interface CompetitionTestBucketMenuItem {
    ref: string,
    name: string,
    offset: number,
    capacity: number,
    attributes: any//Map<string, any>
}

export interface CompetitionGeneralSettings {
    name: string,
    start: string,
    end?: string,

    location?: CompetitionLocation,
    facility?: RefWithName,
    bannerUrl?: string;

    responsible?: RefWithName,
    organizer?: CompetitionOrganizer

    description?: string;
    remarksVisitors?: string;
    remarksVip?: string;
    remarksTrailers?: string;
    remarksTrucks?: string;
}

export interface CompetitionLiveSettings {
    autoLive: boolean;
    autoPublish: boolean;
    publishRate?: number;
}

export interface CompetitionFinanceSettings {
    priceProfiles?: Array<PriceProfile>;
    costCenter: Contact
}

export interface CompetitionRegistrationSettingsResponse {
    settings: CompetitionRegistrationSettings;
    rules?: Object
}

export interface CompetitionRegistrationSettings {
    registrationByOrganisation: boolean;
    registrationByIndividual: boolean;
    registrationPeriod: CompetitionRegistrationPeriod;
    maxTotalRegistrations?: number,
    maxRiderRegistrations: number,
    maxHorseRegistrations: number,
    maxCombinationRegistrations: number,
    registrationSettingsPerSportTypeVariant: boolean
}

export interface CompetitionRegistrationPeriod {
    open?: string;
    openTime?: string;
    close?: string;
    closeTime?: string;
    late?: string;
    lateTime?: string
}

export interface CompetitionSpecialSettings {
    channel?: string;
    additionalSettings?: string;
}


export interface CompetitionPublishRequest {
    includeTimeschedule: boolean;
    includeStartlists: boolean;
    includeStartlistStatusUpdate: boolean;
    startlistStatusUpdate: StartlistStatus;
    includeResults: boolean;
}


export interface AppDataRoutesMetadata {
    appIds: Array<string>,
    activities: Array<AppRouteActivity>,
    events: AppEventsMap,
    routes: {
        competitions: Array<CompetitionAppRoute>,
        activities: Array<ActivityAppRoute>
    }
}

export interface AppRouteActivity {
    ref: string,
    name: string,
    arena: RefWithName,
}

export type AppEventsMap = { [appId: string]: Array<string> }

export interface AppRoute {
    type: 'COMPETITION' | 'ACTIVITY',
    ref?: string,
    name: string,
    appIds: Array<string>,
    contextId: AppRouteContextId,
    allEvents: boolean,
    events: Array<string>
}

export interface AppRouteContextId {
    type: 'ARENA' | 'CUSTOM';
    id?: string;
}

export interface CompetitionAppRoute extends AppRoute {
}

export interface ActivityAppRoute extends AppRoute {
    activityRefs: Array<string>
}

export const manageCompetitionService = new ManageCompetitionService();
